import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useAppStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { redirect } from "@tanstack/react-router";

export const Route = createFileRoute("/signup")({
  head: () => ({
    meta: [
      { title: "Sign Up — PataTasks" },
      {
        name: "description",
        content: "Create your PataTasks account to start accepting app and web testing tasks.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async () => {
    const store = useAppStore.getState();
    if (!store.authInitialized) {
      await store.initAuth();
    }
    if (store.isAuthenticated) {
      throw redirect({ to: "/" });
    }
  },
  component: SignupPage,
});

function SignupPage() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");
  const signup = useAppStore((s) => s.signup);
  const isLoading = useAppStore((s) => s.isLoading);
  const error = useAppStore((s) => s.error);
  const clearError = useAppStore((s) => s.clearError);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setFormError("");

    if (!fullName || !password) {
      setFormError("Please fill in all required fields");
      return;
    }

    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      setFormError("Enter a valid email");
      return;
    }

    if (phone) {
      const normalizedDigits = phone.replace(/\D/g, "");
      if (!(normalizedDigits.length === 10 && normalizedDigits.startsWith("0"))) {
        setFormError("Phone must be 10 digits and start with 0");
        return;
      }
    }

    if (!email && !phone) {
      setFormError("Please provide either email or phone number");
      return;
    }

    const success = await signup(fullName, email || undefined, password, phone || undefined);
    if (success) {
      toast.success("Account created successfully!", { duration: 2000 });
      navigate({ to: "/onboarding" });
    } else {
      const msg = error || "Signup failed";
      setFormError(msg);
      toast.error(msg, { duration: 2000 });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-linear-to-br from-slate-50 via-blue-50/30 to-background px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-linear-to-br from-blue-700 to-indigo-700 text-white shadow-xl">
            <img
              src="/patataskslogo.png"
              alt="PataTasks Logo"
              className="h-12 w-12 rounded-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Create your account</h1>
          <p className="mt-1 text-sm text-slate-500">
            Join PataTasks and start earning from app & web testing
          </p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          {formError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {formError}
            </div>
          )}
          <div className="mb-4">
            <Label htmlFor="fullName" className="mb-1.5 text-slate-700">
              Full Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="John Doe"
              disabled={isLoading}
              className="bg-slate-50"
            />
          </div>
          <div className="mb-4">
            <Label htmlFor="email" className="mb-1.5 text-slate-700">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
              disabled={isLoading}
              className="bg-slate-50"
            />
          </div>
          <div className="mb-4">
            <Label htmlFor="phone" className="mb-1.5 text-slate-700">
              Phone Number
            </Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0712345678"
              disabled={isLoading}
              className="bg-slate-50"
            />
            <p className="mt-1 text-xs text-slate-500">Enter at least one: email or phone</p>
          </div>
          <div className="mb-6">
            <Label htmlFor="password" className="mb-1.5 text-slate-700">
              Password <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoading}
                className="bg-slate-50 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-2 text-slate-400"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Creating account..." : "Sign Up"}
          </Button>
          <p className="mt-4 text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-primary hover:underline">
              Log in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
